'use client';

import Image from 'next/image';
import React from 'react';
import { LocalizedLink } from '@/components/LocalizedLink';
import { FavoriteGameButton } from '@/components/FavoriteGameButton';
import { SafeGameImage } from '@/components/SafeGameImage';
import { useTranslations } from '@/hooks/useTranslations';
import type { GameForFrontend } from '@/lib/cms';
import { getGameImageCandidates } from '@/lib/game-image';
import { getContentImageMeta } from '@/lib/image-meta';

import { getProviderLogoUrl } from '@/lib/provider-image';

function ProviderBadge({ provider, providerSlug }: { provider: string; providerSlug: string }) {
  const [showFallback, setShowFallback] = React.useState(false);
  if (!providerSlug || showFallback) {
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider text-white drop-shadow-md">
        {provider}
      </span>
    );
  }
  return (
    <Image
      src={getProviderLogoUrl(providerSlug)}
      alt={provider}
      title={provider}
      width={40}
      height={40}
      sizes="40px"
      className="h-10 w-10 object-contain"
      onError={() => setShowFallback(true)}
    />
  );
}

type Props = {
  game: GameForFrontend;
};

export function HomeGameCard({ game }: Props) {
  const { t } = useTranslations();
  const imageMeta = getContentImageMeta({
    pageTitle: t('games.title'),
    imageNumber: Number(game.id) || 1,
  });

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-xl border border-white/5 bg-[#1e2235] transition-all duration-300 hover:border-[#ffb74d]/50 hover:shadow-lg">
      <div className="relative overflow-hidden bg-black">
        <div className="absolute left-2 top-2 z-10 flex h-10 w-10 items-center justify-center">
          <ProviderBadge provider={game.provider} providerSlug={game.providerSlug} />
        </div>
        <div className="absolute right-2 top-2 z-10">
          <FavoriteGameButton gameSlug={game.slug} className="bg-black/50 backdrop-blur-sm hover:bg-black/70" />
        </div>
        <LocalizedLink
          href={`/games/${game.slug}`}
          className="block w-full"
          aria-label={t('games.playDemoForName', { name: game.title })}
        >
          <SafeGameImage
            src={game.image}
            srcCandidates={getGameImageCandidates(game.providerSlug, game.slug, 'card')}
            alt={imageMeta.alt}
            title={imageMeta.title}
            imageClassName="block h-auto w-full transition-transform duration-500 group-hover:scale-105"
            placeholderTitle={game.title}
            placeholderContainerClassName="aspect-[4/3] w-full"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-50" />
        </LocalizedLink>
      </div>
      <div className="flex flex-1 flex-col p-3">
        <LocalizedLink
          href={`/games/${game.slug}`}
          className="line-clamp-2 text-center text-sm font-bold text-white transition-colors hover:text-[#ffb74d]"
        >
          {game.title}
        </LocalizedLink>
        <LocalizedLink
          href={`/games/${game.slug}`}
          className="mt-auto w-full rounded-lg bg-[#ffb74d] py-2 text-center text-xs font-bold text-[#4a2800] transition-colors hover:bg-amber-400"
        >
          {t('games.playDemo')}
        </LocalizedLink>
      </div>
    </article>
  );
}
